import React, { useEffect, useCallback, useState, useRef } from "react";
import { Button, Container, Form, Image, Spinner } from "react-bootstrap";
import { DateCalendar, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { FaRegCalendar } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import { Popper } from "@mui/material";
import dayjs from "dayjs";

import "bootstrap/dist/css/bootstrap.min.css";
import "./basic-details.css";

import logo2 from "../../../assets/images/logo2.png";
import backarrow from "../../../assets/images/backarrow.jpg";
import responsivebg from "../../../assets/images/responsive-bg.png";

import Stepper from "../Stepper";
import * as userActions from "../../../actions/user";
import * as userPreferencesActions from "../../../actions/userPreferences";
import { useCookies } from "../../../hooks/useCookies";
import { useAppContext } from "../../../Context/UseContext";
import Loader from '../../Utility/Loader';

// Name validation function
const validateName = (name) => {
  if (!name) {
    return "Name is required.";
  }
  if (name.length < 2) {
    return "Name must be at least two letters long";
  }
  if (!/^[a-zA-Z\s]+$/.test(name)) {
    return "Please enter a valid name (letters only)";
  }
  return "";
};

// Age validation function
const validateAge = (birthday) => {
  if (!birthday) {
    return "Please select your birth date.";
  }
  const age = dayjs().diff(dayjs(birthday), "years");
  if (age <= 18) {
    return "You must be over 19 to join MTD.";
  }
  return "";
};

export const BasicDetails = () => {
  const navigate = useNavigate();
  const isFetched = useRef(false);

  const { getCookie } = useCookies();
  const token = getCookie("token");
  const { userDetails, setUserDetails } = useAppContext();
  const [errorMessage, setErrorMessage] = useState("");
  const [showInfo, setShowInfo] = useState(false);
  const [age, setAge] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [errorBirthday, setErrorBirthday] = useState("");
  const [loading, setLoading] = useState(false);
  const [loader, setLoader] = useState(false);

  const open = Boolean(anchorEl);

  const fetchUserDetails = useCallback(async () => {
    try {
      setLoader(true);
      const data = await userActions.getNameDetails();
      setUserDetails(data);

      if (data.birthday) {
        setSelectedDate(dayjs(data.birthday));
        calculateAge(data.birthday);
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
    } finally {
      setLoader(false);
    }
  }, [setUserDetails]);

  useEffect(() => {
    if (isFetched.current) return;
    isFetched.current = true;

    if (!token) return;
    fetchUserDetails();
  }, [token, fetchUserDetails]);

  const handleNameChange = useCallback((e) => {
    setErrorMessage("");
    const value = e.target.value.replace(/[^a-zA-Z\s]/g, "");
    setUserDetails(prevDetails => ({
      ...prevDetails,
      first_name: value.charAt(0).toUpperCase() + value.slice(1).toLowerCase(),
    }));
  }, [setUserDetails]);

  const handleLastNameChange = useCallback((e) => {
    const value = e.target.value.replace(/[^a-zA-Z\s]/g, "");
    setUserDetails(prevDetails => ({
      ...prevDetails,
      last_name: value.charAt(0).toUpperCase() + value.slice(1).toLowerCase(),
    }));
  }, [setUserDetails]);

  const handleBirthdayChange = useCallback((value) => {
    const date = dayjs(value);
    if (date.isValid()) {
      setSelectedDate(date);
      setUserDetails(prevDetails => ({
        ...prevDetails,
        birthday: date.format("YYYY-MM-DD"),
      }));
      calculateAge(date);
    }
  }, [setUserDetails]);

  const calculateAge = useCallback((birthday) => {
    setErrorBirthday("");
    const birthDate = new Date(birthday);
    const today = new Date();
    let calculatedAge = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      calculatedAge--;
    }
    setAge(calculatedAge);
  }, []);

  const updatePreferences = useCallback(async (calculatedAge) => {
    try {
      let age_from = Math.max(calculatedAge - 5, 18);
      let age_to = calculatedAge + 5;

      await userPreferencesActions.updateAge(
        JSON.stringify({
          age_from,
          age_to,
        })
      );
    } catch (error) {
      console.error("Error updating preferences:", error);
      throw new Error("Failed to update preferences");
    }
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    if (loading) return;

    setErrorMessage("");
    setErrorBirthday("");

    // Validate name
    const nameError = validateName(userDetails.first_name);
    if (nameError) {
      setErrorMessage(nameError);
      return;
    }

    // Validate birthday
    const birthdayError = validateAge(userDetails.birthday);
    if (birthdayError) {
      setErrorBirthday(birthdayError);
      return;
    }

    setLoading(true);
    try {
      const calculatedAge = dayjs().diff(dayjs(userDetails.birthday), "years");
      await updatePreferences(calculatedAge);
      await userActions.updateNameDetails(JSON.stringify(userDetails));
      navigate("/abtyourself");
    } catch (error) {
      console.error("Error updating profile:", error);
      setErrorMessage("An error occurred while updating your profile.");
    } finally {
      setLoading(false);
    }
  }, [userDetails, loading, navigate, updatePreferences]);

  const handleClick = useCallback((event) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  }, [anchorEl]);

  const toggleInfoVisibility = useCallback(() => {
    setShowInfo(!showInfo);
  }, [showInfo]);

  const handleBackNavigation = useCallback(() => {
    window.history.back();
  }, []);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div className="basicdetails-container">
        {open && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "transparent",
              zIndex: 10,
            }}
            onClick={() => setAnchorEl(null)}
          />
        )}
        <div className="basic-details-bg">
          <Image className="banner-image" src={responsivebg} alt="Background" />
        </div>
        {loader && <Loader />}
        <Container className="basicdetails-main">
          <Container className="basicdetails-content">
            <Container className="logo-progressbar4">
              <Container className="logo-arrow4">
                <Image
                  src={backarrow}
                  className="backarrow"
                  onClick={handleBackNavigation}
                  alt="Go back"
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleBackNavigation();
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                />
                <Image
                  src={logo2}
                  alt="Logo"
                  className="logo"
                  style={{ backgroundColor: "transparent" }}
                />
              </Container>
              <Stepper activeStep="BasicDetails" />
            </Container>
            <Container className="basic-details-text">
              <p>Tell us about yourself</p>
            </Container>
            <Container className="basic-details-details">
              <Form onSubmit={handleSubmit} className="basic-details-form">
                <Form.Group className="basic-details-group">
                  <Form.Label className="basic-details-label">
                    What's your name?{" "}
                    <span className="required-asterisk text-danger">*</span>
                  </Form.Label>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Form.Control
                      className={`basic-details-input custom-input ${errorMessage ? "error" : ""}`}
                      type="text"
                      placeholder="First name"
                      value={userDetails.first_name || ""}
                      onChange={handleNameChange}
                      style={{ flex: 1 }}
                      autoComplete="off"
                      onCopy={(e) => e.preventDefault()}
                      onPaste={(e) => e.preventDefault()}
                      onCut={(e) => e.preventDefault()}
                      disabled={loading}
                    />
                  </div>
                  <div style={{ marginTop: "5px" }}>
                    {errorMessage && (
                      <Form.Text className="text-danger error-message">
                        {errorMessage}
                      </Form.Text>
                    )}
                  </div>
                </Form.Group>

                <Form.Group className="basic-details-group purplebox">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Form.Control
                      className="basic-details-input-verify custom-input"
                      type="text"
                      placeholder="Last name (Optional)"
                      value={userDetails.last_name || ""}
                      onChange={handleLastNameChange}
                      style={{
                        flex: 1,
                        marginTop: "12px",
                        marginBottom: "24px",
                      }}
                      autoComplete="off"
                      onCopy={(e) => e.preventDefault()}
                      onPaste={(e) => e.preventDefault()}
                      onCut={(e) => e.preventDefault()}
                      disabled={loading}
                    />
                    <div className="last-name-why-div">
                      <Button
                        className="last-name-why"
                        onClick={toggleInfoVisibility}
                        disabled={loading}
                      >
                        ?
                      </Button>
                    </div>
                  </div>
                  <Container className={`why-info ${showInfo ? "" : "why-info-hidden"}`}>
                    <span>Why last name?</span>
                    <p>
                      It helps us to build a trusted and authentic community for
                      you and others. Only your first name is shown publicly.
                    </p>
                  </Container>
                </Form.Group>

                <Form.Group className="basic-details-group">
                  <Form.Label className="basic-details-label">
                    When is your birthday?{" "}
                    <span className="required-asterisk text-danger">*</span>
                  </Form.Label>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      position: "relative",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                        cursor: "pointer",
                        border: "1px solid black",
                        borderRadius: "10px",
                        overflow: "hidden",
                      }}
                      onClick={handleClick}
                    >
                      <Form.Control
                        className={`basic-details-input-verify custom-input ${errorBirthday ? "error" : ""}`}
                        type="text"
                        placeholder="Year / Month / Day"
                        value={userDetails.birthday || ""}
                        style={{
                          flex: 1,
                          border: errorBirthday ? 1 : 0,
                          borderColor: errorBirthday ? "red" : "transparent",
                          cursor: "pointer",
                        }}
                        readOnly
                        disabled={loading}
                      />
                      <FaRegCalendar style={{ marginRight: "10px" }} />
                    </div>
                    <Popper
                      sx={{
                        backgroundColor: "white",
                        zIndex: 9999,
                        borderRadius: "10px",
                        border: "1px solid black",
                      }}
                      open={open}
                      anchorEl={anchorEl}
                      placement="bottom-start"
                    >
                      <DateCalendar
                        value={selectedDate}
                        onChange={(value) => {
                          handleBirthdayChange(dayjs(value).format("YYYY-MM-DD"));
                        }}
                        disableFuture
                      />
                    </Popper>
                  </div>
                  <div style={{ marginTop: "5px" }}>
                    {errorBirthday && (
                      <Form.Text className="text-danger error-message">
                        {errorBirthday}
                      </Form.Text>
                    )}
                    {age !== null && age !== 54 && (
                      <span className="calculated-age">
                        Your age will be displayed as {age}
                      </span>
                    )}
                  </div>
                </Form.Group>

                <div
                  className="d-flex justify-content-center"
                  style={{
                    position: "fixed",
                    bottom: "30px",
                    width: "100%",
                    gap: "30px",
                  }}
                >
                  <button
                    type="submit"
                    className="global-next-bottom-fix-btn"
                    disabled={loading}
                  >
                    {loading ? (
                      <Spinner animation="border" size="sm" role="status" />
                    ) : (
                      "Next"
                    )}
                  </button>
                </div>
              </Form>
            </Container>
          </Container>
        </Container>
      </div>
    </LocalizationProvider>
  );
};

export default BasicDetails;
